export type ODDIconDef = {
  color: string
  /** Full SVG content inside the 32x32 viewBox */
  svg: string
}

/**
 * Overwatch-style ODD icons: uniform circular badge (30px circle with 1px
 * white stroke), white symbol inside. Viewbox is 32×32.
 */
export const ODD_ICON_DEFS: Record<string, ODDIconDef> = {
  // Red octagon — white double border, bold white "STOP"
  STOP_SIGN: {
    color: '#CF1020',
    svg: `<polygon points="10,1 22,1 31,10 31,22 22,31 10,31 1,22 1,10"
            fill="#CF1020" stroke="white" stroke-width="1.8"/>
          <polygon points="11.5,3.5 20.5,3.5 28.5,11.5 28.5,20.5 20.5,28.5 11.5,28.5 3.5,20.5 3.5,11.5"
            fill="none" stroke="white" stroke-width="0.9"/>
          <text x="16" y="20" text-anchor="middle"
            font-size="8" font-weight="900"
            font-family="Arial Black,Arial,sans-serif"
            fill="white" letter-spacing="0.3">STOP</text>`,
  },

  // All-way stop: same octagon but "ALL" top, "WAY" small below
  ALL_WAY_STOP: {
    color: '#CF1020',
    svg: `<polygon points="10,1 22,1 31,10 31,22 22,31 10,31 1,22 1,10"
            fill="#CF1020" stroke="white" stroke-width="1.8"/>
          <polygon points="11.5,3.5 20.5,3.5 28.5,11.5 28.5,20.5 20.5,28.5 11.5,28.5 3.5,20.5 3.5,11.5"
            fill="none" stroke="white" stroke-width="0.9"/>
          <text x="16" y="15.5" text-anchor="middle"
            font-size="8" font-weight="900"
            font-family="Arial Black,Arial,sans-serif"
            fill="white" letter-spacing="0.3">ALL</text>
          <text x="16" y="24" text-anchor="middle"
            font-size="5.5" font-weight="700"
            font-family="Arial,sans-serif"
            fill="white" letter-spacing="0.5">WAY</text>`,
  },

  // Yellow diamond — black traffic-light housing with R/Y/G circles
  TRAFFIC_LIGHT: {
    color: '#F5C518',
    svg: `<polygon points="16,1 31,16 16,31 1,16"
            fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <rect x="12.5" y="7" width="7" height="18" rx="2" fill="#222"/>
          <circle cx="16" cy="10.5" r="2" fill="#E53935"/>
          <circle cx="16" cy="16" r="2" fill="#F5C518" stroke="#555" stroke-width="0.5"/>
          <circle cx="16" cy="21.5" r="2" fill="#43A047"/>`,
  },

  // Red inverted triangle — white inner border, white outline
  YIELD_SIGN: {
    color: '#CF1020',
    svg: `<polygon points="16,29 1.5,5 30.5,5"
            fill="#CF1020" stroke="white" stroke-width="1.8" stroke-linejoin="round"/>
          <polygon points="16,26 5,7 27,7"
            fill="white" stroke="white" stroke-width="0.5" stroke-linejoin="round"/>
          <polygon points="16,23.5 7,8.5 25,8.5"
            fill="none" stroke="#CF1020" stroke-width="1.5" stroke-linejoin="round"/>`,
  },

  // Yellow diamond — circular arrow with arrowhead
  ROUNDABOUT: {
    color: '#F5C518',
    svg: `<polygon points="16,1 31,16 16,31 1,16"
            fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <circle cx="16" cy="16" r="6.5" fill="none" stroke="#222" stroke-width="2.2"/>
          <path d="M16 9.5 L18.8 12.5 L13.2 12.5Z" fill="#222"/>`,
  },

  // Yellow diamond — pedestrian walking figure
  PEDESTRIAN_CROSSING: {
    color: '#F5C518',
    svg: `<polygon points="16,1 31,16 16,31 1,16"
            fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <circle cx="16" cy="8" r="2.3" fill="#222"/>
          <path d="M16 10.5 L16 20 M12.5 13.5 L19.5 13.5 M16 20 L13 26 M16 20 L19 26"
            stroke="#222" stroke-width="2.2" stroke-linecap="round" fill="none"/>`,
  },

  // Yellow circle — bold X crossing + "RR"
  RAILROAD_CROSSING: {
    color: '#F5C518',
    svg: `<circle cx="16" cy="16" r="14.5" fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <line x1="7" y1="7" x2="25" y2="25" stroke="#222" stroke-width="3" stroke-linecap="round"/>
          <line x1="25" y1="7" x2="7" y2="25" stroke="#222" stroke-width="3" stroke-linecap="round"/>
          <text x="10.5" y="19" font-size="6.5" font-weight="900"
            font-family="Arial Black,Arial,sans-serif" fill="#222">R</text>
          <text x="18" y="19" font-size="6.5" font-weight="900"
            font-family="Arial Black,Arial,sans-serif" fill="#222">R</text>`,
  },

  // White square — red no-entry circle/slash over right-turn arrow
  NO_TURN_ON_RED: {
    color: '#FFFFFF',
    svg: `<rect x="1" y="1" width="30" height="30" rx="2"
            fill="white" stroke="#999" stroke-width="1"/>
          <path d="M11 22 L11 14 Q11 10 15 10 L19 10"
            stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <path d="M17 7.5 L21 10.5 L17 13.5"
            stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <circle cx="16" cy="16" r="9.5" fill="none" stroke="#CF1020" stroke-width="2.2"/>
          <line x1="9.5" y1="22.5" x2="22.5" y2="9.5" stroke="#CF1020" stroke-width="2.2"/>`,
  },

  // Red circle — white horizontal bar (DO NOT ENTER / no entry)
  NO_ENTRY: {
    color: '#CF1020',
    svg: `<circle cx="16" cy="16" r="14.5" fill="#CF1020" stroke="white" stroke-width="1.8"/>
          <rect x="5.5" y="12.5" width="21" height="7" rx="1" fill="white"/>`,
  },

  // Black rectangle — white right arrow + "ONE WAY"
  ONE_WAY: {
    color: '#111111',
    svg: `<rect x="1" y="6.5" width="30" height="19" rx="1.5"
            fill="#111" stroke="white" stroke-width="1.5"/>
          <path d="M6 16 L20 16 M17.5 12 L22.5 16 L17.5 20"
            stroke="white" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  },

  // Blue square — white bus silhouette
  BUS_STOP: {
    color: '#0277BD',
    svg: `<rect x="1" y="1" width="30" height="30" rx="3" fill="#0277BD"/>
          <rect x="7" y="9" width="18" height="13" rx="2"
            fill="none" stroke="white" stroke-width="1.8"/>
          <line x1="7" y1="15" x2="25" y2="15" stroke="white" stroke-width="1.5"/>
          <circle cx="11.5" cy="24.5" r="2.5" fill="white"/>
          <circle cx="20.5" cy="24.5" r="2.5" fill="white"/>
          <rect x="11" y="5.5" width="10" height="5" rx="1.2" fill="white"/>`,
  },

  // Green circle — white left-turn curved arrow
  PROTECTED_LEFT: {
    color: '#2E7D32',
    svg: `<circle cx="16" cy="16" r="14.5" fill="#2E7D32" stroke="white" stroke-width="1.2"/>
          <path d="M22 22 Q22 12 14 12"
            stroke="white" stroke-width="2.5" stroke-linecap="round" fill="none"/>
          <path d="M10 9 L14 12 L10 15"
            stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  },

  // Green circle — white right-turn curved arrow
  PROTECTED_RIGHT: {
    color: '#2E7D32',
    svg: `<circle cx="16" cy="16" r="14.5" fill="#2E7D32" stroke="white" stroke-width="1.2"/>
          <path d="M10 22 Q10 12 18 12"
            stroke="white" stroke-width="2.5" stroke-linecap="round" fill="none"/>
          <path d="M22 9 L18 12 L22 15"
            stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  },

  // Yellow diamond — two converging arrows (merge)
  MERGE: {
    color: '#F5C518',
    svg: `<polygon points="16,1 31,16 16,31 1,16"
            fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <path d="M10 22 L16 14 L22 22" stroke="#222" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <line x1="16" y1="21" x2="16" y2="27"
            stroke="#222" stroke-width="2.2" stroke-linecap="round"/>`,
  },

  // Yellow diamond — two child figures (school zone)
  SCHOOL_ZONE: {
    color: '#F5C518',
    svg: `<polygon points="16,1 31,16 16,31 1,16"
            fill="#F5C518" stroke="#333" stroke-width="1.2"/>
          <circle cx="13" cy="9.5" r="1.9" fill="#222"/>
          <path d="M13 12 L13 18.5 M10.5 14 L15.5 14 M13 18.5 L11 23 M13 18.5 L15 23"
            stroke="#222" stroke-width="1.9" stroke-linecap="round" fill="none"/>
          <circle cx="20" cy="9.5" r="1.9" fill="#222"/>
          <path d="M20 12 L20 18.5 M17.5 14 L22.5 14 M20 18.5 L18 23 M20 18.5 L22 23"
            stroke="#222" stroke-width="1.9" stroke-linecap="round" fill="none"/>`,
  },

  // Right on red — green circle with right-turn arrow + red "R" badge
  RIGHT_ON_RED: {
    color: '#2E7D32',
    svg: `<circle cx="16" cy="16" r="14.5" fill="#2E7D32" stroke="white" stroke-width="1.2"/>
          <path d="M10 22 Q10 12 18 12"
            stroke="white" stroke-width="2.5" stroke-linecap="round" fill="none"/>
          <path d="M22 9 L18 12 L22 15"
            stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <circle cx="24" cy="24" r="6" fill="#CF1020"/>
          <text x="24" y="28" text-anchor="middle"
            font-size="7" font-weight="900"
            font-family="Arial Black,Arial,sans-serif"
            fill="white">R</text>`,
  },
}

export const DEFAULT_ODD_ICON: ODDIconDef = {
  color: '#757575',
  svg: `<circle cx="16" cy="16" r="14.5" fill="#757575" stroke="white" stroke-width="1.8"/>
        <circle cx="16" cy="16" r="4" fill="white"/>`,
}

export function getOddLegendSvg(type: string): string {
  const def = ODD_ICON_DEFS[type] ?? DEFAULT_ODD_ICON
  return `<svg width="18" height="18" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">${def.svg}</svg>`
}

export const getOddIconSvg = (type: string, size: number): string => {
  const def = ODD_ICON_DEFS[type] ?? DEFAULT_ODD_ICON
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><g>${def.svg}</g></svg>`
}

export const getOddIconDataUrl = (type: string, size = 30): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(getOddIconSvg(type, size))}`

/** PNG icon paths matching overwatch's public/assets/*.png */
export const ODD_PNG_MAP: Record<string, string> = {
  STOP_SIGN: '/assets/odd/StopSignWithText.png',
  ALL_WAY_STOP: '/assets/odd/AllWayStopSign.png',
  YIELD_SIGN: '/assets/odd/YieldSign.png',
  NO_ENTRY: '/assets/odd/NoEntrySign.png',
  PEDESTRIAN_CROSSING: '/assets/odd/PedestrianCrossingsSign.png',
  RAILROAD_CROSSING: '/assets/odd/RailroadCrossingsSign.png',
  NO_TURN_ON_RED: '/assets/odd/NoRedTurnSign.png',
  ONE_WAY: '/assets/odd/OneWaySign.png',
  ROUNDABOUT: '/assets/odd/RoundaboutSign.png',
  TRAFFIC_LIGHT: '/assets/odd/TrafficLightSign.png',
  BUS_STOP: '/assets/odd/busStopSign.png',
  SCHOOL_ZONE: '/assets/odd/schoolZoneSign.png',
  RIGHT_ON_RED: '/assets/odd/rightOnRed.png',
}

export const getOddIconSrc = (type: string, size = 30): string =>
  ODD_PNG_MAP[type] ?? getOddIconDataUrl(type, size)

/** Creates a DOM element for a route-map ODD marker. */
export function createOddElementMarker(
  type: string,
  name: string | null,
): HTMLElement {
  const el = document.createElement('div')
  el.style.cursor = 'pointer'
  el.title = name ?? type

  const img = document.createElement('img')
  img.src = getOddIconSrc(type)
  img.width = 30
  img.height = 30
  img.style.display = 'block'
  img.alt = name ?? type
  el.appendChild(img)

  return el
}
